// WS8 WS-5 — TanStack Query hooks for the TPS (Third-Party Servicer)
// registry + per-loan assignment. Mutations invalidate by the tpsKeys
// prefix; the loan-assignment mutation also reaches into the existing
// portfolioAdminKeys.loanDetail key so the LoanHeader/Overview re-fetch
// the new tpsId.

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  reassignTpsForLoan,
  restampTpsFeeForLoan,
  tpsApi,
} from "@/api/portfolioAdmin/tps";
import { tpsKeys } from "@/hooks/portfolioAdmin/tpsKeys";
import { monthlyCycleKeys } from "@/hooks/portfolioAdmin/loanStatementsKeys";
import { portalAbsorptionKeys } from "@/hooks/portalAbsorption/portalAbsorptionKeys";
import { portfolioAdminKeys } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { servicingKeys } from "@/hooks/portfolioAdmin/useServicing";
import type {
  LinkedPortalAccountDto,
  TpsCreate,
  TpsDto,
  TpsListQuery,
  TpsSetupDto,
  TpsStatus,
  TpsUpdate,
} from "@/types/tps";

export function useTpsList(query?: TpsListQuery, enabled = true) {
  return useQuery({
    queryKey: tpsKeys.list(query),
    queryFn: () => tpsApi.list(query),
    enabled,
  });
}

export function useTps(id: string | undefined) {
  return useQuery<TpsDto>({
    queryKey: tpsKeys.detail(id ?? ""),
    queryFn: () => tpsApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateTps() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TpsCreate) => tpsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tpsKeys.all });
    },
  });
}

export function useUpdateTps(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TpsUpdate) => tpsApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tpsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: tpsKeys.all });
    },
  });
}

export function useDeleteTps() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tpsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tpsKeys.all });
    },
  });
}

/** #2 — page-level status toggle (deactivate/reactivate) usable from a list
 * row action where a per-id `useUpdateTps(id)` hook can't be called. */
export function useSetTpsStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: TpsStatus }) =>
      tpsApi.update(id, { status }),
    onSuccess: (_row, { id }) => {
      qc.invalidateQueries({ queryKey: tpsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: tpsKeys.all });
    },
  });
}

export function useSetDefaultTps() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tpsApi.setDefault(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tpsKeys.all });
    },
  });
}

/**
 * Reassign a loan to a different TPS. Invalidates:
 *   - the loan-detail cache (loan.tpsId mirror on the summary)
 *   - the new TPS detail (assigned-loans count card)
 *   - the TPS list (counts column)
 *   - the loan's servicing-fee configs (SP-3 stamps a new row on assignment)
 *   - the loan's Monthly Cycle cockpit (cycle.tpsReady gates the "no servicing
 *     platform assigned" blocking message — stale until reload otherwise)
 */
export function useReassignLoanTps(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tpsId }: { tpsId: string }) =>
      reassignTpsForLoan(loanId, tpsId),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: portfolioAdminKeys.loanDetail(loanId) });
      qc.invalidateQueries({ queryKey: tpsKeys.detail(updated.id) });
      qc.invalidateQueries({ queryKey: tpsKeys.all });
      qc.invalidateQueries({ queryKey: servicingKeys.feeConfigs(loanId) });
      qc.invalidateQueries({ queryKey: monthlyCycleKeys.loanPrefix(loanId) });
    },
  });
}

/** SP-3 — the TPS detail setup checklist (server-derived step states). */
export function useTpsSetup(id: string | undefined) {
  return useQuery<TpsSetupDto>({
    queryKey: tpsKeys.setup(id ?? ""),
    queryFn: () => tpsApi.setup(id!),
    enabled: !!id,
  });
}

/** SP-3 — batched readiness for the reassign/switch pickers. */
export function useTpsReadiness(enabled = true) {
  return useQuery({
    queryKey: tpsKeys.readiness,
    queryFn: () => tpsApi.readiness(),
    enabled,
  });
}

/** SP-3 — explicit "re-stamp from TPS schedule" on the loan's fee-config
 * card (TPS schedule edits never auto-propagate to stamped loans). */
export function useRestampLoanTpsFee(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => restampTpsFeeForLoan(loanId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: servicingKeys.feeConfigs(loanId) });
    },
  });
}

/** Invalidates every read the setup checklist composes from. */
function invalidateTpsSetupScope(
  qc: ReturnType<typeof useQueryClient>,
  id: string,
) {
  void qc.invalidateQueries({ queryKey: tpsKeys.setup(id) });
  void qc.invalidateQueries({ queryKey: tpsKeys.readiness });
  void qc.invalidateQueries({ queryKey: tpsKeys.detail(id) });
}

/** SP-3 — checklist action: certify the TPS (Portfolio.CertifyTps, ADMIN). */
export function useCertifyTps(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (engagementAgreementDocumentId?: string | null) =>
      tpsApi.certify(id, engagementAgreementDocumentId),
    onSuccess: () => invalidateTpsSetupScope(qc, id),
  });
}

/** Portal absorption (WS-D2) — the linked-portal-account reverse lookup
 * feeding the shared PortalAccountCard / AccountNotificationsPanel. */
export function useTpsPortalAccount(id: string | undefined) {
  return useQuery<LinkedPortalAccountDto>({
    queryKey: tpsKeys.portalAccount(id ?? ""),
    queryFn: () => tpsApi.portalAccount(id!),
    enabled: !!id,
  });
}

/** SP-3 — checklist action: send (or re-send) the TPS portal invite. */
export function useInviteTpsPortal(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => tpsApi.portalInvite(id),
    onSuccess: () => {
      invalidateTpsSetupScope(qc, id);
      // WS-D2 — the invite mints/refreshes the linked portal account, so the
      // reverse lookup + the shared PortalAccountCard must refresh too.
      void qc.invalidateQueries({ queryKey: tpsKeys.portalAccount(id) });
      void qc.invalidateQueries({ queryKey: portalAbsorptionKeys.all });
    },
  });
}

/** SP-3 — checklist action: provision the two Stripe Connect accounts and
 * get the Stripe-hosted onboarding links. */
export function useProvisionTpsConnect(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => tpsApi.connectProvision(id),
    onSuccess: () => invalidateTpsSetupScope(qc, id),
  });
}

/** SP-3 — live Connect status re-sync (the webhook-outage escape hatch the
 * checklist's refresh button + the ?connect=return landing use). Not a
 * useQuery: each call re-fetches from Stripe, so it's fired explicitly. */
export function useRefreshTpsConnectStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => tpsApi.connectStatus(id),
    onSuccess: () => invalidateTpsSetupScope(qc, id),
  });
}

/** Section 2 (#7a) — checklist action: provision (idempotently) and EMAIL the
 * firm its Stripe onboarding link(s), instead of staff copying raw links. */
export function useEmailTpsOnboardingLink(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => tpsApi.connectEmailOnboardingLink(id),
    // Emailing provisions the accounts, which can change the connect step state.
    onSuccess: () => invalidateTpsSetupScope(qc, id),
  });
}
